/**
 * The slow movement the graph keeps up while nobody is touching it.
 *
 * A still network reads as a diagram; a network that shifts very slightly
 * reads as something alive. Organizations wander slowly around where they
 * rest, each on its own path, so the arrangement itself keeps shifting. Their
 * rings of people go with them and swell and settle as they go, anyone shared
 * between organizations is carried by all of them at once, and the whole
 * network eases outward and back. The amounts are kept modest: nothing travels
 * far enough to look like it is going somewhere, and a click still lands where
 * it looks like it should.
 */
import type { Point } from "./networkGraph.model";

/** How far a ring swells, as a share of its radius. */
export const RING_SWELL = 0.03;
/** How far an organization wanders from its resting place, in graph units. */
export const WANDER = 4;
/** How far a person not in any ring drifts on their own, in graph units. */
export const DRIFT = 1.4;
/** How much the whole network swells about its centre, as a share of distance. */
export const NETWORK_SWELL = 0.01;
const RING_PERIOD_S = 7;
const WANDER_PERIOD_S = 17;
const DRIFT_PERIOD_S = 11;
const NETWORK_PERIOD_S = 13;

/** A fixed number in [0, 1) for an id, so each thing keeps its own rhythm. */
export function phaseOf(id: string): number {
  let hash = 2166136261;
  for (let index = 0; index < id.length; index += 1) {
    hash = Math.imul(hash ^ id.charCodeAt(index), 16777619);
  }
  return ((hash >>> 0) % 1000) / 1000;
}

/** How far something has moved from where it rests, at `seconds`. */
function loop(amount: number, period: number, phase: number, seconds: number): Point {
  const angle = 2 * Math.PI * (seconds / period + phase);
  return {
    x: Math.cos(angle) * amount,
    // A different rate on each axis, so the path is a slow loop, not a line.
    y: Math.sin(angle * 0.8 + phase * 5) * amount,
  };
}

/** The small movement of a person who is in nobody's ring. */
export function drift(phase: number, seconds: number): Point {
  return loop(DRIFT, DRIFT_PERIOD_S, phase, seconds);
}

/**
 * How far an organization has wandered from its resting place. This is the
 * movement that changes the picture: organizations slowly shift against one
 * another, and their rings and the people between them go with them.
 */
export function wander(phase: number, seconds: number): Point {
  const slow = loop(WANDER * 0.7, WANDER_PERIOD_S, phase, seconds);
  // A second, quicker turn on top keeps the path from repeating too plainly.
  const quick = loop(WANDER * 0.3, WANDER_PERIOD_S * 0.41, phase * 3 + 0.2, seconds);
  return { x: slow.x + quick.x, y: slow.y + quick.y };
}

/** A point carried outward and back as the whole network breathes. */
export function swollen(point: Point, centre: Point, seconds: number): Point {
  const swell = 1 + NETWORK_SWELL * Math.sin((2 * Math.PI * seconds) / NETWORK_PERIOD_S);
  return {
    x: centre.x + (point.x - centre.x) * swell,
    y: centre.y + (point.y - centre.y) * swell,
  };
}

/** Where something that rests at `rest` is once it has moved by `by`. */
export function moved(rest: Point, by: Point): Point {
  return { x: rest.x + by.x, y: rest.y + by.y };
}

/** The average of several movements: what carries a person several organizations share. */
export function shared(movements: Point[]): Point {
  if (!movements.length) return { x: 0, y: 0 };
  return {
    x: movements.reduce((sum, by) => sum + by.x, 0) / movements.length,
    y: movements.reduce((sum, by) => sum + by.y, 0) / movements.length,
  };
}

/**
 * Where somebody in an organization's ring is at `seconds`: carried along with
 * the organization, and a little nearer or further as the ring breathes.
 */
export function ringed(
  rest: Point,
  organizationRest: Point,
  organizationNow: Point,
  organizationPhase: number,
  seconds: number
): Point {
  const swell =
    1 + RING_SWELL * Math.sin(2 * Math.PI * (seconds / RING_PERIOD_S + organizationPhase));
  return {
    x: organizationNow.x + (rest.x - organizationRest.x) * swell,
    y: organizationNow.y + (rest.y - organizationRest.y) * swell,
  };
}
